/**
 * The bar above every report: the period, then the ways out (print, CSV,
 * PDF), always in the same place so nobody hunts for them.
 *
 * Quick periods are one segmented control whose current choice is shown
 * as selected; under each date field is the same date in Hijri.
 */
import { useState, type ReactNode } from 'react'
import {
  Alert, Box, Button, ButtonBase, CircularProgress, Menu, MenuItem, Snackbar, Stack,
  TextField, Typography,
} from '@mui/material'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdfOutlined'
import DownloadIcon from '@mui/icons-material/Download'
import PrintIcon from '@mui/icons-material/PrintOutlined'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { downloadReportPdf, fetchCapabilities } from '../api/endpoints'
import { errorMessage } from '../api/client'
import { activePreset, type PresetName, type Range } from './useDateRange'
import { toHijri } from './hijri'
import { c } from '../theme'

interface Props {
  range: Range
  setRange: (r: Range) => void
  presets: { today: () => void; month: () => void; year: () => void; quarter?: () => void }
  onExport?: () => void
  singleDate?: boolean
  /** No date fields or presets: the report picks its own period (a
   *  VAT quarter) through `children`. */
  hideDates?: boolean
  children?: ReactNode
  /** Server-rendered PDF: path under /reports plus its query parameters. */
  pdf?: { path: string; params?: Record<string, string | undefined>
          filename?: string }
  /** Hide the Print button (a report that is not meant for paper). */
  noPrint?: boolean
}

function DateField({ label, value, onChange }:
{ label: string; value: string; onChange: (v: string) => void }) {
  const { i18n } = useTranslation()
  return (
    <Box>
      <TextField type="date" size="small" label={label} value={value}
        InputLabelProps={{ shrink: true }}
        onChange={(e) => onChange(e.target.value)} sx={{ width: 168 }} />
      <Typography sx={{ fontSize: 11.5, color: c.inkMuted, mt: 0.4, px: 0.25,
                        minHeight: 16 }}>
        {value ? toHijri(value, i18n.language) : ''}
      </Typography>
    </Box>
  )
}

export default function ReportToolbar(
  { range, setRange, presets, onExport, singleDate, hideDates, children,
    pdf, noPrint }: Props,
) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const [pdfError, setPdfError] = useState<string | null>(null)

  // Ask the server whether it can render PDFs. Offering a button that
  // fails is worse than not offering it: the user cannot tell whether the
  // problem is their data or the software.
  const caps = useQuery({
    queryKey: ['capabilities'], queryFn: fetchCapabilities,
    staleTime: 5 * 60_000, retry: false,
  })
  const pdfAvailable = caps.data?.pdf !== false

  async function getPdf(lang: 'en' | 'ar') {
    if (!pdf) return
    setAnchor(null)
    setBusy(true)
    try {
      await downloadReportPdf(pdf.path, { ...pdf.params, lang },
        pdf.filename ?? `${pdf.path.replace(/[/.]/g, '-')}-${range.to}.pdf`)
    } catch (e) {
      setPdfError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const active = activePreset(range)
  const quick: { name: PresetName; label: string; run?: () => void }[] = singleDate
    ? [{ name: 'today', label: t('report.today'), run: presets.today }]
    : [
        { name: 'today', label: t('report.today'), run: presets.today },
        { name: 'month', label: t('report.month'), run: presets.month },
        { name: 'quarter', label: t('report.quarter'), run: presets.quarter },
        { name: 'year', label: t('report.year'), run: presets.year },
      ]

  const quiet = { color: c.inkSecondary, borderColor: c.borderStrong, bgcolor: c.surface }

  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start"
      sx={{ px: 2.5, pt: 2, pb: 1.25, flexWrap: 'wrap' }} useFlexGap
      className="no-print">
      {!singleDate && !hideDates && (
        <DateField label={t('report.from')} value={range.from}
          onChange={(v) => setRange({ ...range, from: v })} />
      )}
      {!hideDates && <>
        <DateField label={singleDate ? t('report.asOf') : t('report.to')} value={range.to}
          onChange={(v) => setRange({ ...range, to: v })} />

        <Box role="group" aria-label={t('report.quickPeriod')} sx={{
          display: 'inline-flex', gap: '2px', p: '3px', borderRadius: '8px',
          bgcolor: 'var(--segment-track)' }}>
          {quick.filter((q) => q.run).map((q) => {
            const on = singleDate ? false : active === q.name
            return (
              <ButtonBase key={q.name} onClick={q.run} aria-pressed={on}
                sx={{
                  minHeight: 34, px: 1.5, borderRadius: '6px', fontSize: 13,
                  fontWeight: on ? 600 : 400,
                  color: on ? c.primaryInk : c.inkSecondary,
                  bgcolor: on ? c.surface : 'transparent',
                  boxShadow: on ? '0 1px 2px rgba(22,32,46,0.12)' : 'none',
                  '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: 1 },
                }}>
                {q.label}
              </ButtonBase>
            )
          })}
        </Box>
      </>}

      {children}
      <Box sx={{ flex: 1 }} />

      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
        {!noPrint && (
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}
            sx={{ ...quiet, display: { xs: 'none', sm: 'inline-flex' } }}>
            {t('report.print')}
          </Button>
        )}
        {onExport && (
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={onExport} sx={quiet}>
            CSV
          </Button>
        )}
        {pdf && pdfAvailable && (
          <>
            <Button variant="contained" disabled={busy}
              startIcon={busy ? <CircularProgress size={14} color="inherit" />
                              : <PictureAsPdfIcon />}
              aria-haspopup="menu"
              onClick={(e) => setAnchor(e.currentTarget)}>PDF</Button>
            <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}
              // Without this the menu keeps focus on a closing item while
              // MUI marks the page behind it aria-hidden, which screen
              // readers report as a focus trap.
              disableRestoreFocus={false} closeAfterTransition>
              <MenuItem onClick={() => void getPdf('en')}>English</MenuItem>
              <MenuItem onClick={() => void getPdf('ar')}>العربية</MenuItem>
            </Menu>
          </>
        )}
      </Stack>

      <Snackbar open={!!pdfError} autoHideDuration={8000} onClose={() => setPdfError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setPdfError(null)}>{pdfError}</Alert>
      </Snackbar>
    </Stack>
  )
}
